import {
    BellIcon,
    ChartBarIcon,
    ChatBubbleLeftRightIcon,
    ChevronDoubleRightIcon,
    ClipboardDocumentCheckIcon,
    Cog8ToothIcon,
    FolderIcon,
    HomeIcon,
    UserIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import { ActionType } from "@sp-crm/core";
import { ReminderContext } from "contexts/reminder";
import React, { useCallback, useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { hideMenu } from "store/actions/responsive";
import {
    useFeature,
    useIsAllowed,
    useIsAllowedInWholeTenant,
    useIsMultiRegionUser,
} from "store/selectors/hooks";
import { designSlice } from "store/slices/design";
import { ApplicationState } from "store/state";
import { isAllowed } from "../../util/permissions";

type ExpandableSection = "clients" | "tasks" | "files" | "reports" | "settings";

const isClientsPath = (pathname: string): boolean => {
    return (
        pathname === "/" ||
        pathname === "/pipeline" ||
        pathname === "/timeline" ||
        pathname === "/transfers" ||
        pathname.startsWith("/clients")
    );
};

export const NgSidebarCompactIconsOnly: React.FC = () => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();

    const [expandedSections, setExpandedSections] = useState<ExpandableSection[]>(() => {
        const initialExpanded: ExpandableSection[] = [];
        if (isClientsPath(pathname)) {
            initialExpanded.push("clients");
        }
        if (pathname.startsWith("/files")) {
            initialExpanded.push("files");
        }
        if (pathname.startsWith("/tasks")) {
            initialExpanded.push("tasks");
        }
        if (pathname.startsWith("/reports")) {
            initialExpanded.push("reports");
        }
        if (pathname.startsWith("/settings")) {
            initialExpanded.push("settings");
        }
        return initialExpanded;
    });

    const toggleSection = useCallback((section: ExpandableSection) => {
        setExpandedSections(prev => {
            const collapsing = prev.includes(section);
            if (collapsing) {
                return prev.filter(s => s !== section);
            } else {
                return [...prev, section];
            }
        });
    }, []);

    const handleExpand = useCallback(() => {
        dispatch(designSlice.actions.setCompactSidebarState("expanded"));
    }, [dispatch]);

    const showReferrals = useSelector((state: ApplicationState) =>
        isAllowed(ActionType.ViewReference, state.permissions, state.region),
    );
    const smsEnabled = useFeature("sms");
    const entitiesWithoutActiveTasksEnabled = useFeature("entitiesWithoutActiveTasks");
    const legacyReportsEnabled = useIsAllowed(ActionType.ViewReports);
    const viewAllEnabled = useIsAllowed(ActionType.ViewAllInvoices);
    const viewOwnEnabled = useIsAllowed(ActionType.ViewOwnInvoices);
    const viewCustomReportsEnabled = useIsAllowed(
        ActionType.AdvancedSearchExplicitExecute,
    );

    const isMultiRegionUser = useIsMultiRegionUser();
    const editAgencySettingsAllowed = useIsAllowedInWholeTenant(ActionType.UpdateTenant);
    const editRegionSettingsAllowed = useIsAllowed(ActionType.UpdateRegionSettings);
    const manageUsersAllowed = useIsAllowed(ActionType.SelfManageModifyUser);
    const manageIntegrationsAllowed = useIsAllowedInWholeTenant(
        ActionType.ManageIntegrations,
    );
    const manageWorkflowsAllowed = useIsAllowed(ActionType.CreateWorkflow);

    const showAgencySettings = editAgencySettingsAllowed;
    const showRegionSettings =
        (editRegionSettingsAllowed && isMultiRegionUser) ||
        (editRegionSettingsAllowed && !editAgencySettingsAllowed);

    const isClientsActive = expandedSections.includes("clients");
    const isFilesActive = expandedSections.includes("files");
    const isTasksActive = expandedSections.includes("tasks");
    const isReportsActive = expandedSections.includes("reports");
    const isSettingsActive = expandedSections.includes("settings");

    const { isReminderEnabled, setVisible, reminders } = useContext(ReminderContext);
    const remindersCount = reminders?.length || 0;
    const handleReminderClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            setVisible(true);
            dispatch(hideMenu());
        },
        [setVisible, dispatch],
    );

    return (
        <div className="flex h-full w-16 flex-col bg-white dark:bg-zinc-900 border-r border-zinc-200 dark:border-zinc-700">
            <div className="flex items-center justify-center p-2 border-b border-zinc-200 dark:border-zinc-800">
                <button
                    onClick={handleExpand}
                    className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                    title="Expand sidebar">
                    <ChevronDoubleRightIcon className="w-4 h-4" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto py-3">
                <div className="flex flex-col items-center gap-1.5">
                    {isReminderEnabled && (
                        <Link
                            to="#"
                            onClick={handleReminderClick}
                            className="relative p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                            title="Reminders">
                            <BellIcon className="w-5 h-5" />
                            {remindersCount > 0 && (
                                <span className="absolute top-0.5 right-0.5 flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                                </span>
                            )}
                        </Link>
                    )}

                    <button
                        onClick={() => toggleSection("clients")}
                        className={`p-2 rounded-lg ${
                            pathname.startsWith("/clients") ||
                            pathname === "/" ||
                            pathname === "/pipeline"
                                ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                        }`}
                        title="Clients">
                        <UsersIcon className="w-5 h-5" />
                    </button>

                    {isClientsActive && (
                        <>
                            <Link
                                to="/pipeline"
                                className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                    pathname === "/pipeline" || pathname === "/"
                                        ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                }`}
                                title="Pipeline">
                                P
                            </Link>
                            <Link
                                to="/timeline"
                                className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                    pathname === "/timeline"
                                        ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                }`}
                                title="Timeline">
                                T
                            </Link>
                            <Link
                                to="/clients"
                                className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                    pathname === "/clients"
                                        ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                }`}
                                title="All Clients">
                                A
                            </Link>
                            <Link
                                to="/transfers"
                                className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                    pathname === "/transfers"
                                        ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                }`}
                                title="Transfers">
                                T
                            </Link>
                        </>
                    )}

                    <Link
                        to="/communities"
                        className={`p-2 rounded-lg ${
                            pathname.startsWith("/communities")
                                ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                        }`}
                        title="Communities">
                        <HomeIcon className="w-5 h-5" />
                    </Link>

                    {showReferrals && (
                        <Link
                            to="/references"
                            className={`p-2 rounded-lg ${
                                pathname.startsWith("/references")
                                    ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                    : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                            }`}
                            title="Referrals">
                            <UserIcon className="w-5 h-5" />
                        </Link>
                    )}

                    <button
                        onClick={() => toggleSection("tasks")}
                        className={`p-2 rounded-lg ${
                            pathname.startsWith("/tasks")
                                ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                        }`}
                        title="Tasks">
                        <ClipboardDocumentCheckIcon className="w-5 h-5" />
                    </button>

                    {isTasksActive && (
                        <>
                            <Link
                                to="/tasks"
                                className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                    pathname === "/tasks"
                                        ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                }`}
                                title="Active Tasks">
                                A
                            </Link>
                            {entitiesWithoutActiveTasksEnabled && (
                                <Link
                                    to="/tasks/missing"
                                    className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                        pathname === "/tasks/missing"
                                            ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                            : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                    }`}
                                    title="Entities Without Tasks">
                                    E
                                </Link>
                            )}
                        </>
                    )}

                    <button
                        onClick={() => toggleSection("files")}
                        className={`p-2 rounded-lg ${
                            pathname.startsWith("/files")
                                ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                        }`}
                        title="Files">
                        <FolderIcon className="w-5 h-5" />
                    </button>

                    {isFilesActive && (
                        <>
                            <Link
                                to="/files"
                                className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                    pathname === "/files"
                                        ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                }`}
                                title="Our Files">
                                O
                            </Link>
                            <Link
                                to="/files/signwise"
                                className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                    pathname.startsWith("/files/signwise")
                                        ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                }`}
                                title="Signwise">
                                S
                            </Link>
                        </>
                    )}

                    <button
                        onClick={() => toggleSection("reports")}
                        className={`p-2 rounded-lg ${
                            pathname.startsWith("/reports")
                                ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                        }`}
                        title="Reports">
                        <ChartBarIcon className="w-5 h-5" />
                    </button>

                    {isReportsActive && (
                        <>
                            <Link
                                to="/reports"
                                className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                    pathname === "/reports"
                                        ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                }`}
                                title="Overview">
                                O
                            </Link>
                            {legacyReportsEnabled && (viewAllEnabled || viewOwnEnabled) && (
                                <Link
                                    to="/reports/show/revenue"
                                    className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                        pathname.startsWith("/reports/show/revenue")
                                            ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                            : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                    }`}
                                    title="Client Placement">
                                    C
                                </Link>
                            )}
                            {legacyReportsEnabled && (
                                <Link
                                    to="/reports/show/referrals"
                                    className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                        pathname.startsWith("/reports/show/referrals")
                                            ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                            : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                    }`}
                                    title="Referrals">
                                    R
                                </Link>
                            )}
                            {viewCustomReportsEnabled && (
                                <Link
                                    to="/reports/show/custom"
                                    className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                        pathname.startsWith("/reports/show/custom")
                                            ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                            : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                    }`}
                                    title="Custom Reports">
                                    C
                                </Link>
                            )}
                        </>
                    )}

                    {smsEnabled && (
                        <Link
                            to="/messaging"
                            className={`p-2 rounded-lg ${
                                pathname.startsWith("/messaging")
                                    ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                    : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                            }`}
                            title="Messaging">
                            <ChatBubbleLeftRightIcon className="w-5 h-5" />
                        </Link>
                    )}
                </div>
            </div>

            <div className="border-t border-zinc-200 dark:border-zinc-800 py-3">
                <div className="flex flex-col items-center gap-1.5">
                    <button
                        onClick={() => toggleSection("settings")}
                        className={`p-2 rounded-lg ${
                            pathname.startsWith("/settings")
                                ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                        }`}
                        title="Settings">
                        <Cog8ToothIcon className="w-5 h-5" />
                    </button>

                    {isSettingsActive && (
                        <>
                            <Link
                                to="/settings/account"
                                className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                    pathname.startsWith("/settings/account")
                                        ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                }`}
                                title="My settings">
                                M
                            </Link>
                            <Link
                                to="/settings/templates"
                                className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                    pathname.startsWith("/settings/templates")
                                        ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                }`}
                                title="Email Templates & Signatures">
                                E
                            </Link>
                            {showAgencySettings && (
                                <Link
                                    to="/settings/agency"
                                    className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                        pathname.startsWith("/settings/agency")
                                            ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                            : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                    }`}
                                    title="Agency settings">
                                    A
                                </Link>
                            )}
                            {showRegionSettings && (
                                <Link
                                    to="/settings/region"
                                    className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                        pathname.startsWith("/settings/region")
                                            ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                            : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                    }`}
                                    title="Office settings">
                                    O
                                </Link>
                            )}
                            {manageWorkflowsAllowed && (
                                <Link
                                    to="/settings/workflow"
                                    className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                        pathname.startsWith("/settings/workflow")
                                            ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                            : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                    }`}
                                    title="Workflows">
                                    W
                                </Link>
                            )}
                            {manageUsersAllowed && (
                                <Link
                                    to="/settings/users"
                                    className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                        pathname.startsWith("/settings/users")
                                            ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                            : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                    }`}
                                    title="User accounts">
                                    U
                                </Link>
                            )}
                            {manageIntegrationsAllowed && (
                                <Link
                                    to="/settings/integrations"
                                    className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium ${
                                        pathname.startsWith("/settings/integrations")
                                            ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                                            : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                                    }`}
                                    title="Integrations">
                                    I
                                </Link>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
